/**
 * COMPOSANT: Timeline
 * Affichage de l'historique des événements d'une entité avec filtres et tâches futures
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTimeline } from '@/hooks/useTimeline';
import { useTasks } from '@/hooks/useTasks';
import type { EntityType } from '@/types/timeline';
import { TIMELINE_EVENT_TYPE } from '@/types/timeline';
import { TimelineEvent } from './TimelineEvent';
import { TimelineFilters, type FilterType } from './TimelineFilters';
import { TimelineFutureTasks } from './TimelineFutureTasks';
import { Card } from '../ui/card';
import { RefreshCw, StickyNote, Plus, Search } from 'lucide-react';
import { Button } from '../ui/button';

interface TimelineProps {
  entityType: EntityType;
  entityId: string;
  onAddNote?: () => void;
  onCreateTask?: () => void;
  onEventsLoaded?: (events: any[]) => void; // ✅ Callback pour exposer les events
  onRefreshReady?: (refresh: () => Promise<void>) => void; // ✅ Exposer la fonction refresh
}

export function Timeline({
  entityType,
  entityId,
  onAddNote,
  onCreateTask,
  onEventsLoaded,
  onRefreshReady,
}: TimelineProps) {
  const { events, loading, error, hasMore, refresh, loadMore } = useTimeline(entityType, entityId);
  const tasks = useTasks(entityType, entityId);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fonction de refresh globale qui rafraîchit events + tâches
  const handleRefresh = useCallback(async () => {
    await refresh();
    await tasks.refresh();
  }, [refresh, tasks]);

  // ✅ Exposer les events et refresh au parent
  useEffect(() => {
    if (onEventsLoaded) onEventsLoaded(events);
  }, [events, onEventsLoaded]);

  useEffect(() => {
    // Exposer handleRefresh pour rafraîchir à la fois les événements et les tâches
    if (onRefreshReady) onRefreshReady(handleRefresh);
  }, [handleRefresh, onRefreshReady]);

  // Filtrer événements selon type ET recherche
  const filteredEvents = useMemo(() => {
    let filtered = events;

    // Filtrer par type
    if (activeFilter !== 'all') {
      const typeMap: Record<FilterType, string[]> = {
        all: [],
        calls: [TIMELINE_EVENT_TYPE.CALL_LOGGED],
        emails: [
          TIMELINE_EVENT_TYPE.EMAIL_SENT,
          TIMELINE_EVENT_TYPE.EMAIL_RECEIVED,
          TIMELINE_EVENT_TYPE.EMAIL_BOUNCED,
        ],
        sequences: [TIMELINE_EVENT_TYPE.SEQUENCE_SENT],
        tasks: [
          TIMELINE_EVENT_TYPE.TASK_CREATED,
          TIMELINE_EVENT_TYPE.TASK_COMPLETED,
          TIMELINE_EVENT_TYPE.TASK_RESCHEDULED,
        ],
        data: [
          TIMELINE_EVENT_TYPE.STATUS_CHANGED,
          TIMELINE_EVENT_TYPE.ASSIGNED_USER_CHANGED,
          TIMELINE_EVENT_TYPE.DATA_UPDATED,
          TIMELINE_EVENT_TYPE.CREW_MEMBER_ADDED,
          TIMELINE_EVENT_TYPE.CREW_MEMBER_REMOVED,
          // On garde FIELD_UPDATED pour l'historique mais déprécié
          TIMELINE_EVENT_TYPE.FIELD_UPDATED,
        ],
        // Filtre paiements — regroupe tous les événements liés au tunnel de paiement
        payments: [
          TIMELINE_EVENT_TYPE.PAYMENT_INITIATED,
          TIMELINE_EVENT_TYPE.PAYMENT_METHOD_SELECTED,
          TIMELINE_EVENT_TYPE.PAYMENT_METHOD_CHANGED,
          TIMELINE_EVENT_TYPE.PAYMENT_PAID,
          TIMELINE_EVENT_TYPE.PAYMENT_REFUND_RECORDED,
          TIMELINE_EVENT_TYPE.PAYMENT_FAILED,
          TIMELINE_EVENT_TYPE.PAYMENT_PAYER_IDENTIFIED,
          TIMELINE_EVENT_TYPE.PAYMENT_PAYER_CHANGED,
          TIMELINE_EVENT_TYPE.PAYMENT_REMINDER_SENT,
          TIMELINE_EVENT_TYPE.PAYMENT_LINK_RESENT,
          TIMELINE_EVENT_TYPE.BPI_MANUALLY_VALIDATED,
          TIMELINE_EVENT_TYPE.BED_LINK_GENERATED,
          TIMELINE_EVENT_TYPE.BED_LINK_EXPIRED,
          TIMELINE_EVENT_TYPE.BED_LINK_SENT,
        ],
      };

      filtered = filtered.filter((e) => typeMap[activeFilter]?.includes(e.event_type));
    }

    // Filtrer par terme de recherche
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.title?.toLowerCase().includes(term) ||
          event.description?.toLowerCase().includes(term) ||
          event.created_by_user?.full_name?.toLowerCase().includes(term) ||
          event.created_by_user?.email?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [events, activeFilter, searchTerm]);

  // Séparer tâches futures vs historique ET filtrer selon l'onglet actif
  const futureTasks = useMemo(() => {
    const now = new Date();
    // Toutes les tâches à venir
    const allFutureTasks = tasks.pendingTasks.filter((t) => new Date(t.due_date) > now);

    // Filtrer selon l'onglet actif
    switch (activeFilter) {
      case 'all':
      case 'tasks':
        // Afficher toutes les tâches à venir
        return allFutureTasks;
      case 'calls':
        // Uniquement les tâches de type "call"
        return allFutureTasks.filter((t) => t.type === 'call');
      case 'emails':
        // Uniquement les tâches de type "email"
        return allFutureTasks.filter((t) => t.type === 'email');
      case 'sequences':
        // Pas de tâches de type "sequence" pour l'instant
        return [];
      case 'data':
        // Pas de tâches à venir sur l'onglet Data
        return [];
      case 'payments':
        // Pas de tâches à venir sur l'onglet Paiements
        return [];
      default:
        return allFutureTasks;
    }
  }, [tasks.pendingTasks, activeFilter]);

  // Handlers pour les actions sur les tâches avec refresh de la timeline
  const handleCompleteTask = async (taskId: string) => {
    const success = await tasks.completeTask(taskId);
    if (success) {
      // Rafraîchir à la fois les tâches et la timeline pour voir le nouvel événement
      await handleRefresh();
    }
    return success;
  };

  const handleRescheduleTask = async (taskId: string, daysToAdd: number) => {
    const success = await tasks.rescheduleTask(taskId, daysToAdd);
    if (success) {
      // Rafraîchir à la fois les tâches et la timeline pour voir le nouvel événement
      await handleRefresh();
    }
    return success;
  };

  // Gérer le chargement de plus d'événements en préservant la position de scroll
  const handleLoadMore = useCallback(async () => {
    if (!scrollContainerRef.current || isLoadingMore) return;

    setIsLoadingMore(true);

    // Sauvegarder la position de scroll actuelle
    const scrollContainer = scrollContainerRef.current;
    const scrollTopBefore = scrollContainer.scrollTop;

    // Charger plus d'événements
    await loadMore();

    // Attendre que le DOM soit mis à jour
    requestAnimationFrame(() => {
      if (scrollContainer) {
        // Restaurer exactement la même position de scroll
        // (les nouveaux éléments sont ajoutés en bas, donc la position relative reste la même)
        scrollContainer.scrollTop = scrollTopBefore;
      }
      setIsLoadingMore(false);
    });
  }, [loadMore, isLoadingMore]);

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-zinc-900">Timeline</h3>
        <div className="flex gap-2">
          {onCreateTask && (
            <Button variant="outline" size="sm" onClick={onCreateTask}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle tâche
            </Button>
          )}
          {onAddNote && (
            <Button variant="outline" size="sm" onClick={onAddNote}>
              <StickyNote className="h-4 w-4 mr-2" />
              Ajouter une note
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher d'événements..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
        />
      </div>

      {/* Filtres */}
      <TimelineFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />

      <div ref={scrollContainerRef} className="flex flex-col gap-6 max-h-[58vh] overflow-y-auto">
        {/* État de chargement */}
        {loading && events.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-zinc-500">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Chargement de l'historique...</span>
            </div>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
            Erreur lors du chargement de l'historique
          </div>
        )}

        {/* Liste des tâches à venir - Filtrées selon l'onglet actif */}
        {!loading && !error && futureTasks.length > 0 && (
          <>
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="text-sm text-zinc-500">
                Tâches à venir (du plus proche au plus lointain)
              </span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>
            <TimelineFutureTasks
              tasks={futureTasks}
              onComplete={handleCompleteTask}
              onReschedule={handleRescheduleTask}
            />
          </>
        )}

        {/* Liste des événements */}
        {!loading && !error && filteredEvents.length > 0 && (
          <>
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="text-sm text-zinc-500">
                Historique (du plus récent au plus ancien)
              </span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>
            <div className="space-y-0">
              {filteredEvents.map((event, index) => (
                <TimelineEvent
                  key={event.id}
                  event={event}
                  isLast={index === filteredEvents.length - 1}
                />
              ))}
            </div>
          </>
        )}

        {/* Aucun événement */}
        {!loading && !error && filteredEvents.length === 0 && futureTasks.length === 0 && (
          <div className="text-center py-12 text-sm text-zinc-500">
            Aucun événement pour le moment
          </div>
        )}

        {/* Bouton Charger plus */}
        {!loading && !error && hasMore && (
          <div className="flex justify-center pt-4 pb-2">
            <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? 'Chargement...' : 'Charger plus'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
